import { AnyAction } from "redux"
import { ThunkAction } from "redux-thunk"
import { getText, requestThunk } from "swiipe.portal.shared"
import { endpoints } from "../../data/endpoints"
import { createGuid } from "../../util/stringUtil"
import { StoreState } from "../StoreState"
import { getGatewaysForMerchantThunk } from "./gatewayThunks"
import { getOnboardingStatusThunk } from "./swiipePaymentsThunks"
import { sendServiceConfirmationEmailThunk } from "./swiipeServicesThunks"

export const termsTechDisableOnboardingThunk =
    (swMerchantId: string, webshopId: string, gatewayId: string): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch) => {
        await dispatch(
            requestThunk(endpoints.Payments.patchGateway(gatewayId), {
                data: {
                    webshopId,
                    paymentType: "TermsTech",
                    enabled: false,
                },
            })
        )

        await dispatch(getGatewaysForMerchantThunk(swMerchantId, true))
        await dispatch(getOnboardingStatusThunk(swMerchantId, webshopId, true, ["TermsTech"]))
    }

export interface ITermsTechGetOnboardingDataResponse {}

export const getTermsTechOnboardingDataThunk =
    (webshopId: string): ThunkAction<Promise<ITermsTechGetOnboardingDataResponse>, StoreState, null, AnyAction> =>
    async (dispatch) => {
        return await dispatch(
            requestThunk<ITermsTechGetOnboardingDataResponse>(endpoints.Area42.getOnboardingData, {
                params: {
                    webshopId: webshopId,
                },
            })
        )
    }

export type TTermsTechOnboardingStatus = "Not Started" | "In Progress" | "Submitted" | "Failed" | "Successful" | "Error"
export interface ITermsTechGetOnboardingDetailsResponse {
    onboardingUrl: string
    onboardingStatus: TTermsTechOnboardingStatus
    errorMessage?: string
}

export const getTermsTechOnboardingDetailsThunk =
    (
        swMerchantId: string,
        webshopId: string
    ): ThunkAction<Promise<ITermsTechGetOnboardingDetailsResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.getOnboardingDetails, {
                params: {
                    swMerchantId,
                    webshopId,
                },
            })
        )
    }

export interface ITermsTechOnboardingConnectData {
    swMerchantId: string
    webshopId: string
    clientId: string
    clientSecret: string
    webhookSecret: string
}

export const termsTechOnboardingConnectThunk =
    (data: ITermsTechOnboardingConnectData): ThunkAction<Promise<void>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        await dispatch(
            requestThunk(endpoints.Area42.onboardConnect, {
                data: data,
            })
        )

        await dispatch(
            sendServiceConfirmationEmailThunk(data.swMerchantId, "TermsTechConfirmation", getText("serviceNames.termsTech"))
        )

        await dispatch(getGatewaysForMerchantThunk(data.swMerchantId, true))
        await dispatch(getOnboardingStatusThunk(data.swMerchantId, data.webshopId, true, ["TermsTech"]))
    }

export const allTermsTechDocumentTypes = [
    "TERMS_AGREEMENT",
    "SELLER_INVOICE",
    "FEES_INVOICE",
    "MARKETPLACE_INVOICE",
    "DELIVERY_SLIP",
    "BUYER_INVOICE",
] as const

export type TTermsTechDocumentType = (typeof allTermsTechDocumentTypes)[number]
export interface ITermsTechMockCallResponse {
    success: boolean
    errorMessage: string
}

export const termsTechMockUploadDocumentThunk =
    (orderRef: string, webshopId: string): ThunkAction<Promise<ITermsTechMockCallResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.mockUploadDocument, {
                data: {
                    orderRef,
                    webshopId,
                },
            })
        )
    }

export const termsTechMockTechOfficerApproveDocsThunk =
    (
        orderRef: string,
        webshopId: string,
        isApproved: boolean
    ): ThunkAction<Promise<ITermsTechMockCallResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.mockTechOfficerApprovesDocs, {
                data: {
                    orderRef,
                    webshopId,
                    isApproved,
                },
            })
        )
    }

export const termsTechMockBuyerApproveInvoiceThunk =
    (
        orderRef: string,
        webshopId: string,
        isApproved: boolean
    ): ThunkAction<Promise<ITermsTechMockCallResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.mockBuyerApproveInvoice, {
                data: {
                    orderRef,
                    webshopId,
                    isApproved,
                },
            })
        )
    }

export const termsTechMockBuyerPayInThunk =
    (orderRef: string, webshopId: string): ThunkAction<Promise<ITermsTechMockCallResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.mockBuyerPayIn, {
                data: {
                    orderRef,
                    webshopId,
                },
            })
        )
    }

export const termsTechMockCaptureTransaction =
    (
        orderRef: string,
        amount: number,
        currency: string
    ): ThunkAction<Promise<ITermsTechMockCallResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.mockWebhook, {
                data: {
                    Id: createGuid(),
                    UserId: null,
                    EventType: "TERMSPayoutExecuted",
                    Notes: null,
                    OrderGUID: createGuid(),
                    OrderReference: orderRef,
                    Payload: JSON.stringify({ Amount: amount, CurrencyISO: currency }),
                },
            })
        )
    }

export const termsTechMockSellerPayOutThunk =
    (orderRef: string, webshopId: string): ThunkAction<Promise<ITermsTechMockCallResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.mockSellerPayOut, {
                data: {
                    orderRef,
                    webshopId,
                    ipAddress: "176.22.68.118", // TODO: IVI - get client ip?
                },
            })
        )
    }

export interface ITermsTechOrderStatusResponse {
    status: string
    errorMessage: string
}

export const getTermsTechOrderStatusThunk =
    (
        swMerchantId: string,
        orderRef: string,
        webshopId: string
    ): ThunkAction<Promise<ITermsTechOrderStatusResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.getOrderStatusAtProvider, {
                params: {
                    swMerchantId,
                    orderRef,
                    webshopId,
                },
            })
        )
    }

export interface ITermsTechOrderReferenceResponse {
    orderReference: string
}
export const getTermsTechOrderReferenceThunk =
    (
        swMerchantId: string,
        webshopId: string,
        orderId: string
    ): ThunkAction<Promise<ITermsTechOrderReferenceResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.getOrderReference, {
                params: {
                    swMerchantId,
                    webshopId,
                    swOrderId: orderId,
                },
            })
        )
    }

export interface ITermsTechOrderDetailsResponse {
    ignoreTestEnvWebhooks: boolean
}
export const getTermsTechOrderDetailsThunk =
    (swMerchantId: string, orderRef: string): ThunkAction<Promise<ITermsTechOrderDetailsResponse>, StoreState, null, AnyAction> =>
    async (dispatch, getState) => {
        return await dispatch(
            requestThunk(endpoints.Area42.getOrderDetails, {
                params: {
                    swMerchantId,
                    orderRef,
                },
            })
        )
    }
